import { useState } from 'react';
import type { ReactNode, ReactNodeArray } from 'react';
import { Dropdown, MenuItem, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useToggle, useEventListener } from '../../../hooks';
import { Filter, ServiceFilter, ServiceQueryParameters } from '../../../types';
import { containEvent, stopEventPropagation, trimString } from '../../../utils';
import { Icon, icons } from '../../generic';
import Flex from '../../generic/Flex';


type ServiceFilterProps = {
  categories: ServiceFilter[];
  onSelect: (filterType: string, filterValue: string) => void;
  selectedFilter?: Filter;
  query?: ServiceQueryParameters;
  children?: ReactNode | ReactNodeArray;
}

const ServiceFilters = ({ categories, children, query, selectedFilter, onSelect }: ServiceFilterProps) => {
  const [filterString, setFilterString] = useState('');
  const [isFilterSelectOpen, toggleIsFilterSelectOpen] = useToggle(false);

  const onSelectFilter = (filterType: string, filterValue: string) => {
    setFilterString('');
    toggleIsFilterSelectOpen();

    onSelect(filterType, filterValue);
  };

  const handleClick = () => {
    if (isFilterSelectOpen) {
      toggleIsFilterSelectOpen();
    }
  };

  // click handler being added to `window`
  useEventListener('click', handleClick);

  return (
    <Dropdown
      open={isFilterSelectOpen}
      id="service-filters"
      className="service-import-header-btn"
    >
      <Dropdown.Toggle
        bsRole="toggle"
        onClick={containEvent(() => {
          toggleIsFilterSelectOpen();
        })}
        noCaret
      >
        {selectedFilter
          ? (
            <Flex justify="space-between">
              {selectedFilter.icon ? (
                <img
                  src={selectedFilter.icon}
                  style={{
                    maxHeight: 24,
                    maxWidth: 24,
                    borderRadius: '2px',
                    marginRight: '10px',
                  }}
                  alt=""
                />
              ) : (
                <Icon icon={icons.circleThin} />
              )}
              <div className="oneline">{selectedFilter.name}</div>
              <div className="caret" />
            </Flex>
          )
          : '...'}
      </Dropdown.Toggle>
      <Dropdown.Menu
        onClick={stopEventPropagation}
      >
        <div style={{ maxHeight: '500px' }}>
          <div className="input-search-wrapper">
            <input
              type="text"
              className="form-control input-search input-block-level"
              placeholder="Search"
              value={filterString}
              onChange={ev => setFilterString(ev.target.value)} />
          </div>
          <hr className="nomargin-v" />
          <ul className="nav nav-minimal">
            {categories?.map(category => {
              const currentFilters = category.filters?.filter(f =>
                !filterString || f.name.toLowerCase().includes(filterString)) ?? [];

              // If no filters exist for the category, don't show the category
              // i.e. if a user doesn't have an organization, don't show the 'organization'
              if (!category.filters?.length) {
                return;
              }

              // eslint-disable-next-line consistent-return
              return [
                <MenuItem header key={category.key}>
                  {category.category}
                </MenuItem>,
                !currentFilters.length
                  ? (
                    <MenuItem disabled>
                        No results found
                    </MenuItem>)
                  : (
                    currentFilters.map(item => (
                      <MenuItem
                        eventKey={category.key}
                        key={item.value}
                        active={query?.filterType === category.key && query.filterValue === item.value}
                        onSelect={() => onSelectFilter(category.key, item.value)}
                      >
                        <Flex justify="flex-start">
                          {item.icon ? (
                            <img
                              src={item.icon}
                              alt=""
                              style={{
                                maxHeight: 24,
                                maxWidth: 24,
                                borderRadius: '2px',
                                marginRight: '10px',
                              }}
                            />
                          ) : <Icon icon={icons.circleThin} />}
                          <OverlayTrigger
                            placement="right"
                            overlay={item.name.length >= 25 ? <Tooltip style={{ marginLeft: '40px' }}>{item.name}</Tooltip> : <span />}
                          >
                            <span>&nbsp;{trimString(item.name, 25)}</span>
                          </OverlayTrigger>
                        </Flex>
                      </MenuItem>
                    ))),
              ];
            })}
            {children}
          </ul>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ServiceFilters;
